import React, { useState, useRef } from 'react';
import { X, FileText, Upload, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { extractTextFromFile, validateFileSize } from '../../utils/fileTextExtractor';
import { parseResumeWithAI } from '../../utils/openRouterService';
import { TextField } from '@anyemp/ui-kit';
import { mockTestCases } from '../../utils/mockResumeData';

// Константа для использования mock данных (для разработки/тестирования)
const USE_MOCK_DATA = false;

const ResumeParserDrawer = ({ isOpen, onClose, onParsed, formType = 'job-application' }) => {
  const [resumeText, setResumeText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Закрываем drawer
  const handleClose = () => {
    if (!isLoading) {
      setResumeText('');
      setSelectedFile(null);
      onClose();
    }
  };

  // Обработка выбора файла
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      validateFileSize(file, 5);
      setSelectedFile(file);
      toast.success(`File selected: ${file.name}`);
    } catch (error) {
      toast.error(error.message);
      e.target.value = '';
    }
  };

  // Клик на кнопку выбора файла
  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Обработка парсинга
  const handleParse = async () => {

    // Проверка наличия данных
    if (!resumeText.trim() && !selectedFile) {
      toast.error('Please paste resume text or select a file');
      return;
    }

    setIsLoading(true);

    try {
      let textFromTextarea = resumeText.trim();
      let textFromFile = '';
      let isImageMode = false;
      let imagesToParse = null;

      // Якщо вибрано файл - ЗАВЖДИ витягуємо з нього текст (незалежно від textarea)
      if (selectedFile) {
        toast.loading('Extracting text from file...', { id: 'extracting' });
        const extractedData = await extractTextFromFile(selectedFile);
        toast.dismiss('extracting');

        // Перевіряємо, чи повернулись зображення або текст
        if (typeof extractedData === 'object' && extractedData.isImages) {
          // PDF містить тільки зображення - використовуємо Gemini Vision OCR
          isImageMode = true;
          imagesToParse = extractedData.images;
        } else {
          // Звичайний текст з файлу
          textFromFile = extractedData || '';
        }
      }

      // Об'єднуємо текст з textarea та з файлу
      const textParts = [textFromTextarea, textFromFile]
        .filter(t => t && t.trim().length > 0);

      if (textParts.length === 0 && !isImageMode) {
        throw new Error('Please paste resume text or select a file');
      }

      let combinedText = '';
      if (textParts.length > 0) {
        // Якщо є обидва джерела - об'єднуємо з PRIORITY MARKERS
        if (textFromTextarea && textFromFile) {
          combinedText =
            '=== PRIORITY INPUT (USER PROVIDED) ===\n\n' +
            textFromTextarea +
            '\n\n=== SUPPLEMENTARY INPUT (FROM FILE) ===\n\n' +
            textFromFile;
        } else {
          // Якщо тільки одне джерело
          combinedText = textParts[0];
        }
      }

      // Парсинг з допомогою AI
      let parsedData;

      if (USE_MOCK_DATA) {
        // Використовуємо mock дані для тестування
        toast.loading('Using mock data...', { id: 'parsing' });
        await new Promise(resolve => setTimeout(resolve, 500)); // Імітація затримки
        parsedData = mockTestCases.comprehensiveResume;
        toast.dismiss('parsing');
      } else if (isImageMode) {
        // Якщо PDF - тільки зображення, використовуємо Gemini Vision
        toast.loading('Reading resume from images with AI (OCR)...', { id: 'parsing' });
        parsedData = await parseResumeWithAI(imagesToParse, true, formType);
        toast.dismiss('parsing');
      } else {
        // Звичайний текстовий парсинг
        toast.loading('Parsing resume with AI...', { id: 'parsing' });
        parsedData = await parseResumeWithAI(combinedText, false, formType);
        toast.dismiss('parsing');
      }

      // Передаем данные родителю
      if (onParsed) {
        onParsed(parsedData);
      }

      // Успешное сообщение
      toast.success('Resume parsed successfully, form filled!', { duration: 4000 });

      // Закрываем drawer
      handleClose();

    } catch (error) {
      toast.dismiss('extracting');
      toast.dismiss('parsing');

      console.error('Error parsing resume:', error);

      // Обработка ошибок с возможностью повторить
      toast.error(
        (t) => (
          <div className="flex flex-col gap-2">
            <div className="font-medium">Failed to parse resume</div>
            <div className="text-sm text-white/90">
              {error.message || 'Unknown error occurred'}
            </div>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                handleParse();
              }}
              className="mt-2 px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        ),
        { duration: 10000 }
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full min-w-0 flex-col border-l border-light-border-primary bg-light-bg-primary shadow-xl transition-transform dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:w-[500px]">
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-light-border-primary p-4 dark:border-dark-border-primary">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              AI Resume Parser
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary dark:hover:text-dark-text-secondary transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {/* Info message */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Paste resume text or upload a file (TXT, PDF, DOC, DOCX). AI will extract information and fill the form automatically.
            </p>
          </div>

          {/* Textarea for text input */}
          <div>
            <TextField
              label="Paste Resume Text"
              multiline
              rows={10}
              fullWidth
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              disabled={isLoading}
              placeholder="Paste resume text here..."
              helperText={resumeText.trim() ? `${resumeText.trim().length} characters` : undefined}
            />
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-light-border-primary dark:border-dark-border-primary"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-light-bg-primary dark:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary">
                AND/OR
              </span>
            </div>
          </div>

          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2">
              Upload File
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                disabled={isLoading}
                className="hidden"
              />
              <button
                onClick={handleFileButtonClick}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-md text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Upload className="w-4 h-4" />
                Choose File
              </button>
              <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary truncate">
                {selectedFile ? selectedFile.name : 'No file selected'}
              </span>
            </div>
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
              Supported formats: TXT, PDF, DOC, DOCX (max 5MB)
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-light-border-primary bg-light-bg-primary p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-dark-border-primary dark:bg-dark-bg-secondary">
          <button
            onClick={handleParse}
            disabled={isLoading || (!resumeText.trim() && !selectedFile)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Parsing Resume...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                Parse Resume
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default ResumeParserDrawer;
